import { useMemo } from 'react';
import { type MarkerEntitiesStyle } from '~/map/map/markers/useMarkers/useMarkerStyles.hook';
import {
  markerSubEntityCount, markerSubEntityZIndexOffsets, type MarkerZIndex,
} from '~/map/zIndexes/markerZIndex';
import { useActiveMarkerIdsSelector } from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import { useMapSettingsMarkersPriorityMarkersSelector } from '~/store/mapSettings/markers/mapSettingsMarkers.selectors';
import {
  type CombinedRowId, type SpreadsheetId, type SpreadsheetRowId,
} from '../../_shared/types/spreadsheetData/spreadsheetRow';
import { logError } from '../../_shared/utils/logError';
import {
  type ReadonlySpreadsheetRowIdMap,
  SpreadsheetRowIdMap,
} from '../../_shared/utils/spreadsheet/spreadsheetRowIdMap';
import { type LatLngRowData } from '../../store/spreadsheetData/spreadsheetData.helpers';
import {
  calculateZIndex,
  ZIndexedEntity,
} from './zIndexRanges';

type PriorityMarkersMap = Map<SpreadsheetId, Map<CombinedRowId, number>>;

const createPriorityMarkersMap = (priorityMarkers: ReadonlyArray<SpreadsheetRowId>): PriorityMarkersMap => {
  const map: PriorityMarkersMap = priorityMarkers.reduce((priorityMap, marker, index) => {
    const spreadsheetPriorityMap = priorityMap.get(marker.spreadsheetId);
    if (spreadsheetPriorityMap) {
      spreadsheetPriorityMap.set(marker.rowId, index);
    }
    else {
      priorityMap.set(marker.spreadsheetId, new Map([[marker.rowId, index]]));
    }

    return priorityMap;
  }, new Map());

  return map;
};

const createComparer = (priorityMarkersMap: PriorityMarkersMap) =>
  (marker1: LatLngRowData, marker2: LatLngRowData): number => {
    const marker1Priority = priorityMarkersMap.get(marker1.spreadsheetId)?.get(marker1.rowId);
    const marker2Priority = priorityMarkersMap.get(marker2.spreadsheetId)?.get(marker2.rowId);

    const marker1IsPriority = marker1Priority !== undefined;
    const marker2IsPriority = marker2Priority !== undefined;

    // If both are priority markers, higher index (later in array) should be higher priority
    if (marker1IsPriority && marker2IsPriority) {
      return (marker1Priority ?? 0) - (marker2Priority ?? 0);
    }

    // If one is priority and the other isn't, priority goes higher (returns positive)
    if (marker1IsPriority && !marker2IsPriority) {
      return 1;
    }
    if (!marker1IsPriority && marker2IsPriority) {
      return -1;
    }

    // Both are non-priority, sort by latitude
    return marker2.lat - marker1.lat;
  };

const calculateEntityOffset = (markerIndex: number, markerEntityIndex: number) => {
  if (markerEntityIndex >= markerSubEntityCount) {
    logError(`calculateEntityOffset: received markerEntityIndex ${markerEntityIndex} is higher than the count of marker entities of ${markerSubEntityCount}. Check what numbers are passed in.`);
  }

  return (markerIndex * markerSubEntityCount) + markerEntityIndex;
};

const createMarkerSubEntityZIndexCalculator = (markerIndex: number, isActive: boolean) =>
  (subEntityOffset: number) => {
    if (isActive) {
      return calculateZIndex(subEntityOffset, ZIndexedEntity.ActiveMarker);
    }
    return calculateZIndex(calculateEntityOffset(markerIndex, subEntityOffset), ZIndexedEntity.Marker);
  };

export const useSpreadsheetMarkerZIndexes = (markers: ReadonlyArray<LatLngRowData>, _markerStyles: ReadonlySpreadsheetRowIdMap<MarkerEntitiesStyle>) => {
  const activeMarkerIds = useActiveMarkerIdsSelector();
  const priorityMarkers = useMapSettingsMarkersPriorityMarkersSelector();

  return useMemo(() => {
    // Create hashmap for efficient priority marker lookups
    const priorityMarkersMap = createPriorityMarkersMap(priorityMarkers);
    const comparer = createComparer(priorityMarkersMap);

    const zIndexEntries = Array.from(markers)
      .sort(comparer)
      .map((marker, markerIndex): readonly [SpreadsheetRowId, MarkerZIndex] => {
        const isActive = activeMarkerIds?.some(activeMarkerId =>
          (marker.spreadsheetId === activeMarkerId?.spreadsheetId) && (marker.rowId === activeMarkerId?.rowId),
        ) ?? false;
        const calculateSubEntityZIndex = createMarkerSubEntityZIndexCalculator(markerIndex, isActive);
        return [marker, {
          shadow: calculateZIndex(markerIndex, ZIndexedEntity.MarkerShadow),
          highlight: calculateSubEntityZIndex(markerSubEntityZIndexOffsets.highlight),
          subMarker: calculateSubEntityZIndex(markerSubEntityZIndexOffsets.subMarker),
          marker: calculateSubEntityZIndex(markerSubEntityZIndexOffsets.marker),
          markerLabelText: calculateSubEntityZIndex(markerSubEntityZIndexOffsets.markerLabelText),
          labelAbove: calculateSubEntityZIndex(markerSubEntityZIndexOffsets.labelAbove),
          labelAboveText: calculateSubEntityZIndex(markerSubEntityZIndexOffsets.labelAboveText),
        }] as const;
      });

    return new SpreadsheetRowIdMap(zIndexEntries).asReadonly();
  }, [activeMarkerIds, markers, priorityMarkers]);
};
